/**
 * A parent managing ONE child's money, on that child's page (2026-10-28).
 *
 * Home's "Money in / Spent" form is for the family at large: pick people, say
 * what it was for. When a parent has already tapped Ravi, the question is
 * narrower -- "give Ravi five", "take two off Ravi", "put some of Ravi's money
 * in his bike" -- and making them pick Ravi again and invent a reason was the
 * friction this removes.
 *
 * WHAT THE CHILD READS. Every entry made here says who did it: "Added by
 * Parent" or "Removed by Parent", with the parent's note after it when they
 * typed one. The reason is sealed for the family and for the child's own key
 * like any other (recordMoney), so it reads the same on the child's device.
 *
 * GOALS. Three moves, all through the routes the Goals screen already uses:
 *   - from their money: the child's own free money set aside in the goal;
 *   - back to their money: the reverse (a parent may, even on a locked goal);
 *   - add your own: new money from the parent. It is written as a family-pot
 *     entry ("Added by Parent for <goal>") and then set aside in the goal as
 *     the family's share -- so it is the parent's gift, not the child's
 *     spending money, and refunding the goal returns it to the family.
 *
 * READ FRESH BEFORE WRITING (2026-11-01). The goal routes take absolute
 * amounts, so a total worked out from what this page last loaded would undo
 * anything that changed since: Ravi putting $3 in from his own device, or an
 * automatic split from an allowance. Each move re-reads the goal first and
 * adds to what is in it NOW.
 *
 * NO ARITHMETIC ON MONEY THAT MATTERS. Like FundsPanel, the goal routes take
 * ABSOLUTE amounts (what each contributor has in the goal afterwards), so the
 * only sums here are "what is in it now, plus what was typed", in whole
 * hundredths, and the server re-checks every one against the ledger.
 */

import { useState } from 'react';
import {
  amountInputMode,
  amountPlaceholder,
  asTyped,
  dollars,
  howMuch,
  looksLikeAmount,
  normaliseAmount,
  shortAmount,
  signed,
} from '../family/money';
import {
  FAMILY_POT,
  loadState,
  recordMoney,
  setGoalFunds,
  type FamilyState,
  type Goal,
  type Person,
} from '../family/state';

/** What every entry made from a child's page begins with. */
export const ADDED_BY_PARENT = 'Added by Parent';
export const REMOVED_BY_PARENT = 'Removed by Parent';

/** "Added by Parent", or "Added by Parent: birthday money" with a note. */
export function parentReason(direction: 'in' | 'out', note: string): string {
  const head = direction === 'in' ? ADDED_BY_PARENT : REMOVED_BY_PARENT;
  const tail = note.trim();
  return tail ? `${head}: ${tail}` : head;
}

const toCents = (typed: string) =>
  looksLikeAmount(typed) ? Math.round(parseFloat(normaliseAmount(typed) || '0') * 100) : 0;

/** Add to or remove from one child's money. */
export function ChildMoney({
  familyId,
  familyKey,
  person,
  free,
  onSaved,
}: {
  familyId: string;
  familyKey: CryptoKey;
  person: Person;
  /** Their money not in any goal, in hundredths. */
  free: number;
  onSaved: () => Promise<void>;
}) {
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [done, setDone] = useState('');
  const [sure, setSure] = useState(false);

  const name = person.name || 'them';
  const cents = toCents(amount);
  // Taking out more than is free leaves their goals as they are and the
  // child owing the difference. Allowed -- it is the parent's call -- but
  // asked about first, once.
  const overdraw = direction === 'out' && cents > Math.max(0, free);

  const label = busy
    ? 'Saving…'
    : !cents
      ? 'Add an amount first'
      : direction === 'in'
        ? `Add ${dollars(cents)} to ${name}`
        : `Remove ${dollars(cents)} from ${name}`;

  async function save(): Promise<void> {
    if (overdraw && !sure) {
      setSure(true);
      return;
    }
    setBusy(true);
    setProblem('');
    setDone('');
    try {
      await recordMoney(
        familyId,
        familyKey,
        { amount: signed(amount, direction), reason: parentReason(direction, note), people: [person.id] },
        [person],
      );
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That did not save.');
      setBusy(false);
      return;
    }
    setDone(direction === 'in' ? `Added ${dollars(cents)}.` : `Removed ${dollars(cents)}.`);
    setAmount('');
    setNote('');
    setSure(false);
    try {
      await onSaved();
    } catch {
      setProblem('Saved, but the latest changes could not load. Reload this page to see them.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="child-money" aria-label={`${person.name || 'Their'} money`}>
      <h2>{person.name ? `${person.name}’s money` : 'Their money'}</h2>
      <div className="record money-entry">
        <div className="segmented" role="group" aria-label="Add or remove">
          <button
            type="button"
            className={direction === 'in' ? 'on' : ''}
            aria-pressed={direction === 'in'}
            onClick={() => {
              setDirection('in');
              setSure(false);
            }}
          >
            Add
          </button>
          <button
            type="button"
            className={direction === 'out' ? 'on' : ''}
            aria-pressed={direction === 'out'}
            onClick={() => {
              setDirection('out');
              setSure(false);
            }}
          >
            Remove
          </button>
        </div>

        <label className="field">
          <span>{howMuch()}</span>
          <input
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setSure(false);
              setDone('');
            }}
            inputMode={amountInputMode()}
            autoComplete="off"
            placeholder={amountPlaceholder()}
            aria-label={direction === 'in' ? `Amount to add to ${name}` : `Amount to remove from ${name}`}
          />
          <small>
            {dollars(Math.max(0, free))} free
            {free < 0 ? ` (${dollars(-free)} owed)` : ''}
          </small>
        </label>

        <div className="quick-amounts" role="group" aria-label="Quick amounts">
          {[1, 2, 5, 10].map((n) => {
            const typed = asTyped(n * 100);
            return (
              <button
                type="button"
                key={n}
                className={normaliseAmount(amount) === normaliseAmount(typed) ? 'quick-amount on' : 'quick-amount'}
                onClick={() => {
                  setAmount(typed);
                  setSure(false);
                  setDone('');
                }}
              >
                {shortAmount(n * 100)}
              </button>
            );
          })}
        </div>

        <label className="field">
          <span>Note (optional)</span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={90}
            autoComplete="off"
            placeholder={direction === 'in' ? 'Birthday money' : 'Lost library book'}
          />
          <small>
            {name === 'them' ? 'They' : name} will see &ldquo;{parentReason(direction, note)}&rdquo;.
          </small>
        </label>

        {sure ? (
          <div className="danger-box" role="alert">
            <p>
              {person.name || 'They'} only {free > 0 ? `has ${dollars(free)}` : 'has nothing'} free. Removing{' '}
              {dollars(cents)} leaves their goals as they are and they will owe{' '}
              {dollars(cents - Math.max(0, free))}.
            </p>
          </div>
        ) : null}
        {problem ? <p className="error">{problem}</p> : null}
        {done && !problem ? <p className="footnote" role="status">{done}</p> : null}

        <button
          type="button"
          className={cents && !busy ? `primary full record-go ${direction}` : 'full record-go waiting'}
          aria-busy={busy}
          disabled={busy || !cents}
          onClick={() => void save()}
        >
          {sure ? `Yes, remove ${dollars(cents)}` : label}
        </button>
      </div>
    </section>
  );
}

type GoalMove = 'from' | 'back' | 'own';

/** A parent's buttons under one of the child's goals. */
export function ParentGoalActions({
  familyId,
  familyKey,
  person,
  goal,
  free,
  state,
  onSaved,
}: {
  familyId: string;
  familyKey: CryptoKey;
  person: Person;
  goal: Goal;
  /** The child's money not in any goal. */
  free: number;
  state: FamilyState;
  onSaved: () => Promise<void>;
}) {
  const [mode, setMode] = useState<GoalMove | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const name = person.name || 'them';
  const mine = goal.funds[person.id] ?? 0;
  const theirs = goal.funds[FAMILY_POT] ?? 0;
  const left = Math.max(0, goal.target - goal.pot);
  const cents = toCents(amount);
  const most = mode === 'from' ? Math.max(0, free) : mode === 'back' ? mine : Infinity;
  const tooMuch = cents > most;
  const close = () => {
    setMode(null);
    setAmount('');
    setProblem('');
  };

  async function save(): Promise<void> {
    if (!mode) return;
    setBusy(true);
    setProblem('');
    let stage = '';
    try {
      // What is in the goal now, not when this page last loaded. See the top.
      const now = (await loadState(familyId, familyKey)).goals.find((one) => one.id === goal.id);
      if (!now || now.status !== 'active') throw new Error('This goal has closed. Refresh to see it.');
      const mineNow = now.funds[person.id] ?? 0;
      const theirsNow = now.funds[FAMILY_POT] ?? 0;
      if (mode === 'from') {
        await setGoalFunds(familyId, goal.id, { [person.id]: asTyped(mineNow + cents) });
      } else if (mode === 'back') {
        if (cents > mineNow) throw new Error(`${person.name || 'They'} only has ${dollars(mineNow)} in it now.`);
        await setGoalFunds(familyId, goal.id, { [person.id]: asTyped(mineNow - cents) });
      } else {
        // New money from the parent: into the family pot, then into the goal
        // as the family's share. Two writes; if the second fails the money is
        // safe in the family pot and the message says so.
        await recordMoney(
          familyId,
          familyKey,
          { amount: asTyped(cents), reason: `${ADDED_BY_PARENT} for ${goal.title || 'a goal'}`, people: [] },
          state.people,
        );
        stage = 'pot';
        // `force`, because this money was deposited a moment ago for exactly
        // this. Without it a family pot that was already below zero (an outing
        // paid for from it) has "less free than the deposit", and the server
        // refused the second write -- leaving the gift in the pot, not the goal.
        await setGoalFunds(familyId, goal.id, { [FAMILY_POT]: asTyped(theirsNow + cents) }, true);
      }
    } catch (error) {
      const why = error instanceof Error ? error.message : 'That did not work.';
      setProblem(
        stage === 'pot'
          ? `The ${dollars(cents)} was added to the family pot, but it could not go into the goal: ${why} You can move it in from Goals.`
          : why,
      );
      setBusy(false);
      if (stage === 'pot') await onSaved().catch(() => {});
      return;
    }
    close();
    try {
      await onSaved();
    } catch {
      setProblem('Saved, but the latest changes could not load. Reload this page to see them.');
    } finally {
      setBusy(false);
    }
  }

  if (!mode) {
    return (
      <div className="secret-actions kid-goal-actions parent-goal-actions">
        {free > 0 ? (
          <button type="button" className="primary" onClick={() => setMode('from')}>
            Move from {person.name ? `${person.name}’s` : 'their'} money
          </button>
        ) : null}
        <button type="button" onClick={() => setMode('own')}>
          Add your own
        </button>
        {mine > 0 ? (
          <button type="button" onClick={() => setMode('back')}>
            Back to {person.name ? `${person.name}’s` : 'their'} money
          </button>
        ) : null}
        {theirs > 0 ? <span className="footnote">You have put in {dollars(theirs)}.</span> : null}
      </div>
    );
  }

  const hint =
    mode === 'from'
      ? tooMuch
        ? `${person.name || 'They'} only ${free > 0 ? `has ${dollars(free)}` : 'has nothing'} free.`
        : `${dollars(Math.max(0, free))} of ${person.name ? `${person.name}’s` : 'their'} money is free to move.`
      : mode === 'back'
        ? tooMuch
          ? `${person.name || 'They'} only put in ${dollars(mine)}.`
          : `Up to ${dollars(mine)} can go back. It becomes ${person.name ? `${person.name}’s` : 'their'} money to spend.`
        : `New money from you, shown as “${ADDED_BY_PARENT}”. It stays the family’s if the goal is refunded.`;

  return (
    <div className="put-in parent-goal-move">
      <label className="field">
        <span>
          {mode === 'from'
            ? `Move how much from ${name}?`
            : mode === 'back'
              ? `Give back how much to ${name}?`
              : 'Add how much?'}
        </span>
        <input
          value={amount}
          onChange={(event) => {
            setAmount(event.target.value);
            setProblem('');
          }}
          inputMode={amountInputMode()}
          autoComplete="off"
          placeholder={amountPlaceholder()}
          aria-label="Amount"
        />
        <small>{hint}</small>
      </label>
      {mode !== 'back' && left > 0 ? (
        <button
          type="button"
          className="text-button"
          onClick={() => setAmount(asTyped(mode === 'from' ? Math.min(left, Math.max(0, free)) : left))}
        >
          {mode === 'from' && free < left ? `Use all ${dollars(Math.max(0, free))}` : `Fill it: ${dollars(left)}`}
        </button>
      ) : null}
      {problem ? <p className="error">{problem}</p> : null}
      <div className="actions">
        <button
          type="button"
          className="primary"
          aria-busy={busy}
          disabled={busy || !cents || tooMuch}
          onClick={() => void save()}
        >
          {busy
            ? 'Saving…'
            : mode === 'from'
              ? `Move ${cents ? dollars(cents) : 'it'} in`
              : mode === 'back'
                ? `Give ${cents ? dollars(cents) : 'it'} back`
                : `Add ${cents ? dollars(cents) : 'it'}`}
        </button>
        <button type="button" disabled={busy} onClick={close}>
          Cancel
        </button>
      </div>
    </div>
  );
}
